import { useLazyQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { clientNeoSociete } from "config/apollo/apollo.config";
import { LIST_COMPANIES } from "queries/neosociete";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

interface AutoCompleteProps {
  placeholder: string;
  filter: string;
  onChange: (e: React.SyntheticEvent, value: OptionItem | string | null) => void;
}

const DEBOUNCE_TIMEOUT = 500;

export default function GenericAutoComplete(props: AutoCompleteProps): React.ReactElement {
  const { placeholder, filter, onChange, ...rest } = props;
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const [options, setOptions] = useState<OptionItem[]>([]);
  const [value] = useDebounce(searchTerm, DEBOUNCE_TIMEOUT);
  const [open, setOpen] = useState(false);

  const [listCompanies, { loading, error }] = useLazyQuery(LIST_COMPANIES, {
    client: clientNeoSociete,
    onCompleted: ({ listCompanies }) => {
      getAutoCompleteOptions(listCompanies);
    },
  });

  useEffect(() => {
    if (value.length >= 3) {
      listCompanies({
        variables: {
          limit: 10,
          [filter]: value,
          orderBy: ["name"],
        },
      });
      setOpen(true);
    }
  }, [value]);

  const getAutoCompleteOptions = data => {
    if (error) {
      setOptions([{ value: t("Error loading options"), key: "error" }]);
      return;
    }

    const formattedOptions =
      data?.results.map(result => ({
        key: result.vatNumber,
        value: result.name,
        vatNumber: result.vatNumber,
        address: result.establishments[0],
        legalStatus: result.legalCategory,
        businessActivity: result.businessActivity,
      })) || [];

    setOptions(formattedOptions);
  };

  const onSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  return (
    <Autocomplete
      options={options}
      loading={loading}
      loadingText={t("Loading...")}
      open={open}
      getOptionLabel={option => (typeof option === "string" ? option : option.value)}
      filterOptions={options => options}
      onInputChange={(e, newValue, reason) => {
        if (reason === "input") {
          setOpen(false);
          setOptions([]);
          onSearch(newValue);
          onChange(e, newValue);
        }
      }}
      onChange={(e, value) => {
        setOpen(false);
        onChange(e, value || null);
      }}
      renderOption={(props, option) => {
        if (loading) {
          return (
            <li {...props} style={{ fontStyle: "italic", color: "gray" }}>
              {t("Loading...")}
            </li>
          );
        }

        return (
          <li {...props} key={option.key}>
            <div className="search">
              <span>
                {option.vatNumber} - {option.value}
              </span>{" "}
              <br />
              <span className="street-name">
                {option?.address?.address} {option?.address?.postalCode}
              </span>
            </div>
          </li>
        );
      }}
      renderInput={params => <TextField {...params} label={placeholder} />}
      {...rest}
    />
  );
}
